import { Component, OnInit, inject, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  FormsModule,
  FormBuilder,
  FormGroup,
  FormControl,
  Validators,
} from '@angular/forms';
import { Router, RouterModule, ActivatedRoute } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { DatePickerModule } from 'primeng/datepicker';
import { CheckboxModule } from 'primeng/checkbox';

import { SkeletonModule } from 'primeng/skeleton';
import { DrawerModule } from 'primeng/drawer';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { MessageService } from 'primeng/api';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  EscalaService,
  PostoService,
  EfetivoService,
  TurnoService,
  ClienteService,
  ContratoService,
  EscalaBulkItemDto,
  PapelEfetivoEscalaEnum,
} from '@core/services/domain-services';
import {
  Posto,
  Efetivo,
  Turno,
  Cliente,
  ContratoCliente,
  EnumGenero,
} from '@core/models/models';
import { StatusBadgeComponent } from '@shared/components/status-badge/status-badge.component';
import { PaginatorControlComponent } from '@shared/components/paginator-control/paginator-control.component';

type PapelEfetivoEscala = 'EFETIVO' | 'SUPERVISOR' | 'VIGILANTE';

interface EfetivoEscalaConfig {
  papelEfetivoEscalaEnum: PapelEfetivoEscala;
}

interface EfetivoSelecao extends Efetivo {
  selected: boolean;
  config?: EfetivoEscalaConfig;
}

@Component({
  selector: 'app-nova-escala',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    RouterModule,
    ButtonModule,
    SelectModule,
    DatePickerModule,
    CheckboxModule,

    SkeletonModule,
    DrawerModule,
    AutoCompleteModule,
    StatusBadgeComponent,
    PaginatorControlComponent,
  ],

  templateUrl: './nova-escala.component.html',
  styleUrl: './nova-escala.component.scss',
})
export class NovaEscalaComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly svc = inject(EscalaService);
  private readonly postoSvc = inject(PostoService);
  private readonly efetivoSvc = inject(EfetivoService);
  private readonly turnoSvc = inject(TurnoService);
  private readonly clienteSvc = inject(ClienteService);
  private readonly contratoSvc = inject(ContratoService);
  private readonly msg = inject(MessageService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);

  // ── Wizard state ─────────────────────────────────────────
  activeStep = 0;

  readonly steps = [
    { label: 'Cliente', icon: 'ri-building-2-line' },
    { label: 'Contrato', icon: 'ri-file-text-line' },
    { label: 'Posto', icon: 'ri-map-pin-2-line' },
    { label: 'Período', icon: 'ri-calendar-line' },
    { label: 'Efetivos', icon: 'ri-team-line' },
  ];

  // ── Step 0: Cliente ───────────────────────────────────────
  clientes: Cliente[] = [];
  clientesFiltrados: Cliente[] = [];
  clienteSelecionado: Cliente | null = null;
  clienteSearchCtrl = new FormControl('');
  loadingClientes = false;

  // ── Step 1: Contrato ──────────────────────────────────────
  contratos: ContratoCliente[] = [];
  contratoSelecionado: ContratoCliente | null = null;
  loadingContratos = false;

  // ── Step 2: Posto ─────────────────────────────────────────
  postos: Posto[] = [];
  postoSelecionado: Posto | null = null;
  loadingPostos = false;

  // ── Step 3: Período ───────────────────────────────────────
  form!: FormGroup;

  readonly coberturaOpts = [
    {
      label: 'Diurno',
      value: 'DIURNO',
      icon: 'ri-sun-line',
      desc: '06h – 18h',
    },
    {
      label: 'Noturno',
      value: 'NOTURNO',
      icon: 'ri-moon-line',
      desc: '18h – 06h',
    },
    {
      label: '24 Horas',
      value: '24_HORAS',
      icon: 'ri-time-line',
      desc: 'Turno integral',
    },
  ];

  // ── Step 4: Efetivos ──────────────────────────────────────
  efetivos: EfetivoSelecao[] = [];
  turnos: Turno[] = [];
  filtroNome = '';
  efetivosFirst = 0;
  efetivosRows = 10;
  submitting = false;
  loadingEfetivos = false;
  loadingDropdowns = false;

  showEfetivoConfigDialog = false;
  efetivoEmConfiguracao: EfetivoSelecao | null = null;
  configAtual: EfetivoEscalaConfig = { papelEfetivoEscalaEnum: 'EFETIVO' };

  readonly papelEfetivoOpts: Array<{
    value: PapelEfetivoEscala;
    label: string;
    desc: string;
    icon: string;
  }> = [
    {
      value: 'EFETIVO',
      label: 'Efetivo',
      desc: 'Executa as tarefas operacionais definidas para o posto.',
      icon: 'ri-shield-user-line',
    },
    {
      value: 'SUPERVISOR',
      label: 'Supervisor',
      desc: 'Coordena a equipa e acompanha o cumprimento da escala.',
      icon: 'ri-user-star-line',
    },
    {
      value: 'VIGILANTE',
      label: 'Vigilante',
      desc: 'Realiza a vigilância e o controlo de segurança do posto.',
      icon: 'ri-eye-line',
    },
  ];

  // ── Mocks ─────────────────────────────────────────────────
  private readonly mockTurnos: Turno[] = [
    { id: 1, designacao: 'Diurno (06h–18h)' },
    { id: 2, designacao: 'Noturno (18h–06h)' },
    { id: 3, designacao: 'Integral (24 horas)' },
  ];
  private readonly mockEfetivosBase: Efetivo[] = [
    {
      id: 1,
      nome: 'João Silva',
      numBI: '001LA036',
      dataNascimento: '1992-03-15',
      genero: EnumGenero.MASCULINO,
      numTelefone: '923 456 789',
      idFuncao: 1,
      idCategoria: 1,
      dataAdmissao: '2020-06-01',
      ativo: true,
    },
    {
      id: 2,
      nome: 'Maria Santos',
      numBI: '002LA036',
      dataNascimento: '1988-05-15',
      genero: EnumGenero.FEMININO,
      numTelefone: '923 555 888',
      idFuncao: 1,
      idCategoria: 1,
      dataAdmissao: '2019-06-01',
      ativo: true,
    },
    {
      id: 3,
      nome: 'Paulo Ferreira',
      numBI: '003LA036',
      dataNascimento: '1990-09-20',
      genero: EnumGenero.MASCULINO,
      numTelefone: '923 777 999',
      idFuncao: 2,
      idCategoria: 1,
      dataAdmissao: '2021-03-15',
      ativo: true,
    },
    {
      id: 4,
      nome: 'Ana Rodrigues',
      numBI: '004LA036',
      dataNascimento: '1995-11-10',
      genero: EnumGenero.FEMININO,
      numTelefone: '923 111 222',
      idFuncao: 1,
      idCategoria: 2,
      dataAdmissao: '2022-07-01',
      ativo: true,
    },
    {
      id: 5,
      nome: 'Carlos Gomes',
      numBI: '005LA036',
      dataNascimento: '1987-03-25',
      genero: EnumGenero.MASCULINO,
      numTelefone: '923 333 444',
      idFuncao: 3,
      idCategoria: 2,
      dataAdmissao: '2018-11-20',
      ativo: true,
    },
    {
      id: 6,
      nome: 'Luísa Baptista',
      numBI: '006LA036',
      dataNascimento: '1993-07-12',
      genero: EnumGenero.FEMININO,
      numTelefone: '923 222 333',
      idFuncao: 1,
      idCategoria: 1,
      dataAdmissao: '2021-09-01',
      ativo: true,
    },
  ];

  ngOnInit(): void {
    this.form = this.fb.group({
      dataInicio: [null, Validators.required],
      dataTermino: [null, Validators.required],
      cobertura: ['DIURNO', Validators.required],
      escalaSubstituicao: [false],
      turnoId: [null, Validators.required],
    });

    this.loadClientes();
    this.loadEfetivos();
    this.loadDropdowns();

    // suporte a ?idPosto=X vindo do posto-detail
    const idPosto = this.route.snapshot.queryParamMap.get('idPosto');
    if (idPosto) {
      this.postoSvc
        .getById(+idPosto)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (p) => {
            this.postoSelecionado = p;
            // saltar os primeiros passos se vier de um posto
            if (p.idCliente) {
              this.loadContratosByCliente(p.idCliente);
              this.activeStep = 2; // ir directo para passo de posto
              this.postos = [p];
            }
          },
          error: () => {},
        });
    }
  }

  // ── Clientes ──────────────────────────────────────────────
  loadClientes(): void {
    this.loadingClientes = true;
    this.clienteSvc
      .getAll({ pageSize: 200 })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.clientes = res.data ?? [];
          this.loadingClientes = false;
        },
        error: () => {
          this.clientes = [];
          this.loadingClientes = false;
        },
      });
  }

  pesquisarClientes(event: any): void {
    const q = (event.query || '').toLowerCase();
    this.clientesFiltrados = q
      ? this.clientes.filter(
          (c) => c.nome.toLowerCase().includes(q) || (c.nif || '').includes(q),
        )
      : this.clientes.slice(0, 20);
  }

  selecionarCliente(ev: any): void {
    const c: Cliente = ev.value ?? ev;
    this.clienteSelecionado = c;
    this.clienteSearchCtrl.setValue(c.nome, { emitEvent: false });
    this.contratoSelecionado = null;
    this.postoSelecionado = null;
    this.postos = [];
    if (c.id) this.loadContratosByCliente(c.id);
  }

  limparCliente(): void {
    this.clienteSelecionado = null;
    this.clienteSearchCtrl.setValue('', { emitEvent: false });
    this.contratoSelecionado = null;
    this.postoSelecionado = null;
    this.contratos = [];
    this.postos = [];
  }

  selecionarClienteChip(c: Cliente): void {
    this.clienteSelecionado = c;
    this.clienteSearchCtrl.setValue(c.nome, { emitEvent: false });
    this.contratoSelecionado = null;
    this.postoSelecionado = null;
    this.postos = [];
    if (c.id) this.loadContratosByCliente(c.id);
  }

  // ── Contratos ────────────────────────────────────────────
  loadContratosByCliente(idCliente: number): void {
    this.loadingContratos = true;
    this.contratoSvc
      .getByCliente(idCliente)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.contratos = res.data ?? (Array.isArray(res) ? res : []);
          this.loadingContratos = false;
        },
        error: () => {
          this.contratos = [];
          this.loadingContratos = false;
        },
      });
  }

  selecionarContrato(c: ContratoCliente): void {
    this.contratoSelecionado = c;
    if (this.clienteSelecionado?.id)
      this.loadPostosByCliente(this.clienteSelecionado.id);
  }

  // ── Postos ───────────────────────────────────────────────
  loadPostosByCliente(idCliente: number): void {
    this.loadingPostos = true;
    this.postoSvc
      .getAll({ idCliente, pageSize: 100 } as any)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.postos = res.data ?? [];
          this.loadingPostos = false;
        },
        error: () => {
          this.postos = [];
          this.loadingPostos = false;
        },
      });
  }

  selecionarPosto(p: Posto): void {
    this.postoSelecionado = p;
  }

  // ── Dropdowns ─────────────────────────────────────────────
  loadDropdowns(): void {
    this.loadingDropdowns = true;
    this.turnoSvc
      .getAll({ pageSize: 100 })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.turnos = res.data?.length ? res.data : this.mockTurnos;
          this.loadingDropdowns = false;
        },
        error: () => {
          this.turnos = this.mockTurnos;
          this.loadingDropdowns = false;
        },
      });
  }

  // ── Efetivos ──────────────────────────────────────────────
  loadEfetivos(): void {
    this.loadingEfetivos = true;
    this.efetivoSvc
      .getAll({ pageSize: 200 })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          const base = res.data?.length ? res.data : this.mockEfetivosBase;
          this.efetivos = base.map((e) => ({ ...e, selected: false }));
          this.loadingEfetivos = false;
        },
        error: () => {
          this.efetivos = this.mockEfetivosBase.map((e) => ({
            ...e,
            selected: false,
          }));
          this.loadingEfetivos = false;
        },
      });
  }

  // ── Wizard navigation ─────────────────────────────────────
  nextStep(): void {
    if (this.activeStep === 0 && !this.clienteSelecionado) {
      this.msg.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: 'Seleccione um cliente',
        life: 3000,
      });
      return;
    }
    if (this.activeStep === 1 && !this.contratoSelecionado) {
      this.msg.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: 'Seleccione um contrato',
        life: 3000,
      });
      return;
    }
    if (this.activeStep === 2 && !this.postoSelecionado) {
      this.msg.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: 'Seleccione um posto',
        life: 3000,
      });
      return;
    }
    if (this.activeStep === 3) {
      this.form.markAllAsTouched();
      if (this.form.invalid) return;
    }
    if (this.activeStep < this.steps.length - 1) this.activeStep++;
  }

  prevStep(): void {
    if (this.activeStep > 0) this.activeStep--;
  }

  goToStep(n: number): void {
    if (n < this.activeStep) this.activeStep = n;
  }

  // ── Computed ──────────────────────────────────────────────
  get efetivosFiltered(): EfetivoSelecao[] {
    const q = this.filtroNome.trim().toLowerCase();
    return q
      ? this.efetivos.filter(
          (e) =>
            e.nome.toLowerCase().includes(q) ||
            (e.numBI || '').toLowerCase().includes(q),
        )
      : this.efetivos;
  }

  get efetivosPaginados(): EfetivoSelecao[] {
    return this.efetivosFiltered.slice(
      this.efetivosFirst,
      this.efetivosFirst + this.efetivosRows,
    );
  }

  get selectedEfetivos(): EfetivoSelecao[] {
    return this.efetivos.filter((e) => e.selected);
  }

  get coberturaLabel(): string {
    const map: Record<string, string> = {
      DIURNO: 'Diurno',
      NOTURNO: 'Noturno',
      '24_HORAS': '24 Horas',
    };
    return map[this.form.get('cobertura')?.value] || '';
  }

  setCobertura(val: string): void {
    this.form.get('cobertura')?.setValue(val);
  }

  onFiltroNomeChange(): void {
    this.efetivosFirst = 0;
  }

  onEfetivosPageChange(event: { first?: number; rows?: number }): void {
    this.efetivosFirst = event.first ?? 0;
    this.efetivosRows = event.rows ?? this.efetivosRows;
  }

  canSubmit(): boolean {
    if (!this.postoSelecionado) return false;
    if (this.form.invalid) return false;
    if (this.selectedEfetivos.length === 0) return false;
    return true;
  }

  // ── Per-efetivo config ─────────────────────────────────────
  toggleEfetivo(e: EfetivoSelecao): void {
    if (e.selected) {
      e.selected = false;
      e.config = undefined;
    } else {
      e.selected = true;
      e.config = { papelEfetivoEscalaEnum: 'EFETIVO' };
    }
  }

  abrirConfigEfetivo(e: EfetivoSelecao): void {
    this.efetivoEmConfiguracao = e;
    this.configAtual = e.config
      ? { papelEfetivoEscalaEnum: e.config.papelEfetivoEscalaEnum }
      : { papelEfetivoEscalaEnum: 'EFETIVO' };
    this.showEfetivoConfigDialog = true;
  }

  confirmarEfetivoConfig(): void {
    if (!this.efetivoEmConfiguracao) return;
    this.efetivoEmConfiguracao.config = {
      papelEfetivoEscalaEnum: this.configAtual.papelEfetivoEscalaEnum,
    };
    this.efetivoEmConfiguracao.selected = true;
    this.showEfetivoConfigDialog = false;
    this.efetivoEmConfiguracao = null;
  }

  cancelarConfigEfetivo(): void {
    this.showEfetivoConfigDialog = false;
    this.efetivoEmConfiguracao = null;
  }

  removerEfetivo(e: EfetivoSelecao): void {
    e.selected = false;
    e.config = undefined;
  }

  clearAll(): void {
    this.efetivos.forEach((e) => {
      e.selected = false;
      e.config = undefined;
    });
  }

  // ── Helpers ───────────────────────────────────────────────
  getInitials(nome: string): string {
    return nome
      .split(' ')
      .filter((n) => n.length > 0)
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }

  getPapelLabel(value?: PapelEfetivoEscala): string {
    return (
      this.papelEfetivoOpts.find((opt) => opt.value === value)?.label ??
      'Efetivo'
    );
  }

  formatDate(d: string | Date | null | undefined): string {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('pt-AO');
  }

  formatContrato(c: ContratoCliente): string {
    return `Contrato #${c.id} · ${this.formatDate(c.inicioContrato)} – ${this.formatDate(c.fimContrato)}`;
  }

  isContratoActivo(c: ContratoCliente): boolean {
    const hoje = new Date();
    return (
      new Date(c.inicioContrato) <= hoje && hoje <= new Date(c.fimContrato)
    );
  }

  // ── Build bulk payload ────────────────────────────────────
  private buildEscalasBulk(): EscalaBulkItemDto[] {
    const v = this.form.value;
    const dataInicio =
      v.dataInicio instanceof Date
        ? this.toApiISOString(v.dataInicio)
        : v.dataInicio;
    const dataTermino =
      v.dataTermino instanceof Date
        ? this.toApiISOString(v.dataTermino)
        : v.dataTermino;
    const turnoId: number = v.turnoId;

    if (!turnoId) {
      this.msg.add({
        severity: 'warn',
        summary: 'Turno obrigatório',
        detail: 'Seleccione um turno.',
        life: 3000,
      });
      throw new Error('turnoId obrigatório');
    }

    const papelMap: Record<string, number> = {
      EFETIVO: 1,
      SUPERVISOR: 2,
      VIGILANTE: 3,
    };

    return [
      {
        idPosto: this.postoSelecionado!.id!,
        turnoId,
        dataInicio,
        dataTermino,
        efetivos: this.selectedEfetivos.map((efetivo) => ({
          efetivoId: efetivo.id!,
          papelEfetivoEscalaEnum:
            papelMap[efetivo.config?.papelEfetivoEscalaEnum ?? 'EFETIVO'] ?? 1,
        })),
      },
    ];
  }

  private toApiISOString(d: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T00:00:00.000Z`;
  }

  save(): void {
    if (!this.canSubmit()) {
      this.form.markAllAsTouched();
      if (this.selectedEfetivos.length === 0) {
        this.msg.add({
          severity: 'warn',
          summary: 'Atenção',
          detail: 'Seleccione pelo menos um efetivo',
          life: 3000,
        });
      }
      return;
    }
    if (this.submitting) return;
    this.submitting = true;

    let dtos: EscalaBulkItemDto[];
    try {
      dtos = this.buildEscalasBulk();
    } catch {
      this.submitting = false;
      return;
    }

    this.svc
      .createBulk(dtos)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.msg.add({
            severity: 'success',
            summary: 'Sucesso',
            detail: `Escala criada para ${this.selectedEfetivos.length} efetivo(s)`,
            life: 3000,
          });
          this.submitting = false;
          setTimeout(() => this.router.navigate(['/admin/escalas']), 1200);
        },
        error: (err) => {
          const detail =
            err?.error?.error ??
            err?.error?.message ??
            'Falha ao criar escala.';
          this.msg.add({
            severity: 'error',
            summary: 'Erro',
            detail,
            life: 5000,
          });
          this.submitting = false;
        },
      });
  }

  voltar(): void {
    this.router.navigate(['/admin/escalas']);
  }
}
